import React, { useState, useEffect } from 'react';
import axios from '../../axios';
import { Table, Container, Button, Modal, Form, Pagination, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import EnrollmentUploadForm from './EnrollmentUploadForm';
import EnrollmentForm from './EnrollmentForm';
import EnrollmentStatusUploadForm from './EnrollmentStatusUploadForm';

const EnrollmentListPage = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showStatusUploadModal, setShowStatusUploadModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [currentAcademicYear, setCurrentAcademicYear] = useState('');
  const [error, setError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [enrollmentToDelete, setEnrollmentToDelete] = useState(null);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false); // State for "Delete All" confirmation
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCurrentAcademicYear = async () => {
      try {
        const res = await axios.get('/configurations/key/current_academic_year');
        setCurrentAcademicYear(res.data.configValue);
        fetchEnrollments(res.data.configValue, 1);
      } catch (err) {
        setError('Không tìm thấy cấu hình current_academic_year');
      }
    };

    fetchCurrentAcademicYear();
  }, []);

  const fetchEnrollments = async (academicYear, page = 1) => {
    try {
      const res = await axios.get('/enrollments', {
        params: { search, page, academicYear }
      });
      setEnrollments(res.data.enrollments);
      setTotalPages(res.data.totalPages);
      setPage(res.data.currentPage);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchEnrollments(currentAcademicYear, 1);
  };

  const handleShowUploadModal = () => {
    setShowUploadModal(true);
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
  };

  const handleShowStatusUploadModal = () => {
    setShowStatusUploadModal(true);
  };

  const handleCloseStatusUploadModal = () => {
    setShowStatusUploadModal(false);
  };

  const handleShowFormModal = (enrollment = null) => {
    setSelectedEnrollment(enrollment);
    setShowFormModal(true);
  };

  const handleCloseFormModal = () => {
    setSelectedEnrollment(null);
    setShowFormModal(false);
    fetchEnrollments(currentAcademicYear, page);
  };

  const handleShowConfirmModal = (enrollmentId) => {
    setEnrollmentToDelete(enrollmentId);
    setShowConfirmModal(true);
  };

  const handleCloseConfirmModal = () => {
    setEnrollmentToDelete(null);
    setShowConfirmModal(false);
  };

  const handleDeleteEnrollment = async (enrollmentId) => {
    try {
      await axios.delete(`/enrollments/${enrollmentId}`);
      fetchEnrollments(currentAcademicYear, page);
      handleCloseConfirmModal();
    } catch (err) {
      console.error('Error deleting enrollment:', err);
      alert('Có lỗi xảy ra khi xóa sinh viên. Vui lòng thử lại.');
    }
  };

  const handlePageChange = (pageNumber) => {
    fetchEnrollments(currentAcademicYear, pageNumber);
  };

  const handleShowDeleteAllModal = () => {
    setShowDeleteAllModal(true);
  };

  const handleCloseDeleteAllModal = () => {
    setShowDeleteAllModal(false);
  };

  const handleDeleteAllEnrollments = async () => {
    try {
      await axios.delete('/enrollments/enrollments'); // Assuming this endpoint deletes all enrollments
      fetchEnrollments(currentAcademicYear, 1);
      handleCloseDeleteAllModal();
    } catch (err) {
      console.error('Error deleting all enrollments:', err);
      alert('Có lỗi xảy ra khi xóa tất cả sinh viên. Vui lòng thử lại.');
    }
  };

  return (
    <Container>
      <h2>Danh sách sinh viên nhập học năm {currentAcademicYear}</h2>
      {error && <Alert variant="danger">{error}</Alert>}
      <Form onSubmit={handleSearchSubmit} className="mb-3">
        <Form.Control
          type="text"
          placeholder="Tìm theo số báo danh, mã sinh viên, căn cước công dân hoặc mã vạch"
          value={search}
          onChange={handleSearchChange}
        />
        <Button variant="primary" type="submit" className="ml-2">Tìm</Button>
      </Form>
      <div className="mb-3">
        <Button variant="primary" onClick={() => handleShowFormModal()}>
          Thêm mới sinh viên
        </Button>
        <Button variant="primary" className="ml-2" onClick={handleShowUploadModal}>
          Upload Enrollments
        </Button>
        <Button variant="secondary" className="ml-2" onClick={handleShowStatusUploadModal}>
          Upload Enrollment Status
        </Button>
        <Button variant="danger" className="ml-2" onClick={handleShowDeleteAllModal}>
          Xóa tất cả
        </Button>
      </div>

      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Số thứ tự</th>
            <th>Số báo danh</th>
            <th>Mã sinh viên</th>
            <th>Họ tên</th>
            <th>Số CCCD/CMND</th>
            <th>Mã vạch</th>
            <th>Số GBNNH</th>
            <th>Ngày nhập học</th>
            <th>Tình trạng</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {enrollments.map((enrollment, index) => (
            <tr key={enrollment._id}>
              <td>{(page - 1) * 20 + index + 1}</td>
              <td>{enrollment.examNumber}</td>
              <td>{enrollment.studentId}</td>
              <td>{enrollment.name}</td>
              <td>{enrollment.citizenId}</td>
              <td>{enrollment.barcode}</td>
              <td>{enrollment.enrollmentReceiptNumber}</td>
              <td>{new Date(enrollment.enrollmentDate).toLocaleDateString()}</td>
              <td>{enrollment.status}</td>
              <td>
                <Button variant="warning" className="mr-2" onClick={() => handleShowFormModal(enrollment)}>
                  Edit
                </Button>
                <Button variant="danger" onClick={() => handleShowConfirmModal(enrollment._id)}>
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Pagination>
        {[...Array(totalPages).keys()].map(number => (
          <Pagination.Item key={number + 1} active={number + 1 === page} onClick={() => handlePageChange(number + 1)}>
            {number + 1}
          </Pagination.Item>
        ))}
      </Pagination>

      <Modal show={showUploadModal} onHide={handleCloseUploadModal}>
        <Modal.Header closeButton>
          <Modal.Title>Import Enrollments from Excel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <EnrollmentUploadForm onUploadSuccess={handleCloseUploadModal} />
        </Modal.Body>
      </Modal>

      <Modal show={showStatusUploadModal} onHide={handleCloseStatusUploadModal}>
        <Modal.Header closeButton>
          <Modal.Title>Upload Enrollment Status from Excel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <EnrollmentStatusUploadForm onUploadSuccess={handleCloseStatusUploadModal} />
        </Modal.Body>
      </Modal>

      <Modal show={showConfirmModal} onHide={handleCloseConfirmModal}>
        <Modal.Header closeButton>
          <Modal.Title>Xác nhận xóa</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Bạn có chắc chắn muốn xóa sinh viên này không? Hành động này không thể hoàn tác.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseConfirmModal}>
            Hủy
          </Button>
          <Button variant="danger" onClick={() => {
            handleDeleteEnrollment(enrollmentToDelete);
            handleCloseConfirmModal();
          }}>
            Xóa
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteAllModal} onHide={handleCloseDeleteAllModal}>
        <Modal.Header closeButton>
          <Modal.Title>Xác nhận xóa tất cả</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Bạn có chắc chắn muốn xóa tất cả sinh viên không? Hành động này không thể hoàn tác.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteAllModal}>
            Hủy
          </Button>
          <Button variant="danger" onClick={handleDeleteAllEnrollments}>
            Xóa tất cả
          </Button>
        </Modal.Footer>
      </Modal>

      <EnrollmentForm
        show={showFormModal}
        onHide={handleCloseFormModal}
        currentAcademicYear={currentAcademicYear}
        enrollment={selectedEnrollment}
        onSave={handleCloseFormModal}
      />
    </Container>
  );
};

export default EnrollmentListPage;
